// "Magic-quadrant" scatter of every tested model: two performance axes, a
// crosshair at the mean of each axis, and the four Gartner-style quadrants.
// Fixed axes: X = Thinking tok/s, Y = Generation tok/s (no user selection).
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OverviewEntry } from '../api';

interface Props {
  models: OverviewEntry[];
  benchLabels: string[];
}

interface Axis {
  key: string;
  label: string;
  /** lower raw value is "better" -> plotted towards the top/right */
  invert?: boolean;
  value: (m: OverviewEntry) => number | null;
  fmt: (n: number) => string;
}

const tps = (n: number) => `${n.toFixed(1)} tok/s`;
const ms = (n: number) => `${Math.round(n)} ms`;
const pct = (n: number) => `${n.toFixed(1)}%`;

function buildAxes(benchLabels: string[]): Axis[] {
  const base: Axis[] = [
    { key: 'generateTps', label: 'Generation tok/s', value: (m) => m.generateTps, fmt: tps },
    { key: 'prefillTps', label: 'Prefill tok/s', value: (m) => m.prefillTps, fmt: tps },
    { key: 'thinkTps', label: 'Thinking tok/s', value: (m) => m.thinkTps, fmt: tps },
    { key: 'ttftMs', label: 'TTFT (ms)', invert: true, value: (m) => m.ttftMs, fmt: ms },
    { key: 'maxKv', label: 'Context (KV)', value: (m) => m.maxKv, fmt: (n) => `${n}` },
    { key: 'gpuPercent', label: 'GPU offload %', value: (m) => m.gpuPercent, fmt: pct },
  ];
  for (const b of benchLabels) {
    base.push({
      key: `bench:${b}`,
      label: b,
      value: (m) => (typeof m.benchmarks?.[b] === 'number' ? (m.benchmarks![b] as number) : null),
      fmt: pct,
    });
  }
  return base;
}

const W = 820;
const H = 560;
const M = { top: 28, right: 24, bottom: 52, left: 60 };
const PW = W - M.left - M.right;
const PH = H - M.top - M.bottom;

function statusColor(s: string | null): string {
  if (s === 'running' || s === 'scheduled') return 'var(--warn, #d9822b)';
  if (s === 'error') return 'var(--err, #e03131)';
  return 'var(--accent, #2d5bdb)';
}

export default function QuadrantChart({ models, benchLabels }: Props) {
  const navigate = useNavigate();
  const axes = useMemo(() => buildAxes(benchLabels), [benchLabels]);

  // Fixed axes: X = Thinking tok/s, Y = Generation tok/s
  const xAxis = axes.find((a) => a.key === 'thinkTps') ?? axes[0];
  const yAxis = axes.find((a) => a.key === 'generateTps') ?? axes[1];

  const points = useMemo(() => {
    const raw = models
      .map((m) => ({ m, x: xAxis.value(m), y: yAxis.value(m) }))
      .filter((p): p is { m: OverviewEntry; x: number; y: number } => p.x != null && p.y != null);

    const xs = raw.map((p) => p.x);
    const ys = raw.map((p) => p.y);
    const range = (vals: number[]) => {
      const lo = Math.min(...vals);
      const hi = Math.max(...vals);
      const pad = (hi - lo || Math.abs(hi) || 1) * 0.12;
      return { lo: lo - pad, hi: hi + pad };
    };
    const xr = xs.length ? range(xs) : { lo: 0, hi: 1 };
    const yr = ys.length ? range(ys) : { lo: 0, hi: 1 };
    const mean = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / (vals.length || 1);

    const sx = (v: number) => {
      const t = (v - xr.lo) / (xr.hi - xr.lo || 1);
      return M.left + (xAxis.invert ? 1 - t : t) * PW;
    };
    const sy = (v: number) => {
      const t = (v - yr.lo) / (yr.hi - yr.lo || 1);
      return M.top + (yAxis.invert ? t : 1 - t) * PH;
    };

    return {
      list: raw.map((p) => ({ ...p, cx: sx(p.x), cy: sy(p.y) })),
      meanX: xs.length ? sx(mean(xs)) : M.left + PW / 2,
      meanY: ys.length ? sy(mean(ys)) : M.top + PH / 2,
    };
  }, [models, xAxis, yAxis]);

  const notPlotted = models.filter((m) => xAxis.value(m) == null || yAxis.value(m) == null);

  return (
    <div className="quadrant">
      <svg
        className="quadrant-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Models by ${xAxis.label} (x) and ${yAxis.label} (y)`}
      >
        {/* plot frame */}
        <rect
          x={M.left}
          y={M.top}
          width={PW}
          height={PH}
          fill="var(--panel, #fff)"
          stroke="var(--border, #d9dce1)"
        />
        {/* quadrant crosshair at the means */}
        <line
          x1={points.meanX}
          y1={M.top}
          x2={points.meanX}
          y2={M.top + PH}
          stroke="var(--border, #d9dce1)"
          strokeDasharray="4 4"
        />
        <line
          x1={M.left}
          y1={points.meanY}
          x2={M.left + PW}
          y2={points.meanY}
          stroke="var(--border, #d9dce1)"
          strokeDasharray="4 4"
        />
        {/* quadrant labels */}
        <text className="quadrant-tag" x={M.left + PW - 8} y={M.top + 16} textAnchor="end">
          Leaders
        </text>
        <text className="quadrant-tag" x={M.left + 8} y={M.top + 16}>
          Challengers
        </text>
        <text className="quadrant-tag" x={M.left + PW - 8} y={M.top + PH - 8} textAnchor="end">
          Visionaries
        </text>
        <text className="quadrant-tag" x={M.left + 8} y={M.top + PH - 8}>
          Niche players
        </text>

        {/* axis titles */}
        <text className="quadrant-axis-title" x={M.left + PW / 2} y={H - 14} textAnchor="middle">
          {xAxis.label} {xAxis.invert ? '(lower is better) →' : '→'}
        </text>
        <text
          className="quadrant-axis-title"
          x={-(M.top + PH / 2)}
          y={16}
          textAnchor="middle"
          transform="rotate(-90)"
        >
          {yAxis.label} {yAxis.invert ? '(lower is better) →' : '→'}
        </text>

        {/* points */}
        {points.list.map(({ m, x, y, cx, cy }) => (
          <g
            key={m.model}
            className="quadrant-point"
            onClick={() => navigate(`/model/${encodeURIComponent(m.model)}`)}
          >
            <title>
              {m.displayName}
              {'\n'}
              {xAxis.label}: {xAxis.fmt(x)}
              {'\n'}
              {yAxis.label}: {yAxis.fmt(y)}
            </title>
            <circle cx={cx} cy={cy} r={6} fill={statusColor(m.lastStatus)} />
            <text x={cx + 10} y={cy + 4} className="quadrant-point-label">
              {m.displayName.length > 22 ? `${m.displayName.slice(0, 21)}…` : m.displayName}
            </text>
          </g>
        ))}
      </svg>

      {notPlotted.length > 0 ? (
        <p className="muted small">
          Not plotted (no {xAxis.label} / {yAxis.label} data yet):{' '}
          {notPlotted.map((m) => m.displayName).join(', ')}
        </p>
      ) : null}
    </div>
  );
}
